import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, ArrowUpRight, CheckCircle2, Clock3, Globe2, Package,
  PauseCircle, RefreshCw, Settings2, TrendingUp, Wrench, XCircle
} from 'lucide-react';
import './DeviceDashboard.css';

const DASHBOARD_API =
  'https://alcobrakeadmin.com/v3/us/cadevices/cagetdevicedashboard?dealerid=31278';

const STATUSES = [
  { key: 'registered', label: 'Registered', description: 'New devices in the pipeline', icon: Package, tone: 'registered' },
  { key: 'undertesting', label: 'Under testing', description: 'Testing currently in progress', icon: Activity, tone: 'testing' },
  { key: 'testcompleted', label: 'Test completed', description: 'Device testing completed', icon: CheckCircle2, tone: 'completed' },
  { key: 'readytoinstall', label: 'Ready to install', description: 'Prepared for installation', icon: Wrench, tone: 'ready' },
  { key: 'continuetesting', label: 'Continue testing', icon: Clock3, tone: 'continue' },
  { key: 'stopped', label: 'Stopped / paused', icon: PauseCircle, tone: 'stopped' },
  // { key: 'installed', label: 'Installed',   description: 'Device successfully installed',icon: CheckCircle2, tone: 'installed' }
];

const PRODUCTION_STATUSES = [
  {
    key: 'registered',
    label: 'Registered',
    description: 'New devices in the pipeline',
    icon: Package,
    tone: 'registered'
  },
  {
    key: 'undertesting',
    label: 'Under testing',
    description: 'Testing currently in progress',
    icon: Activity,
    tone: 'testing'
  },
  {
    key: 'testcompleted',
    label: 'Test completed',
    description: 'Device testing completed',
    icon: CheckCircle2,
    tone: 'completed'
  },
  {
    key: 'readytoinstall',
    label: 'Ready to install',
    description: 'Prepared for installation',
    icon: Wrench,
    tone: 'ready'
  },
  // {
  //   key: 'installed',
  //   label: 'Installed',
  //   description: 'Device successfully installed',
  //   icon: CheckCircle2,
  //   tone: 'installed'
  // }
];
// const formatNumber = value => value.toLocaleString();
const formatNumber = value => (value ?? 0).toLocaleString();

const percent = (value, total) => total > 0 ? Math.min(100, Math.round(value / total * 100)) : 0;

const DeviceDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await fetch(DASHBOARD_API);
      if (!response.ok) throw new Error(`Unable to load dashboard (${response.status})`);
      const data = await response.json();
      const keys = ['getalldevices', 'latest30daysregistered', 'indiaversion', 'usversion', 'indiatestcompleted', 'ustestcompleted', ...STATUSES.map(status => status.key)];
      setDashboard(Object.fromEntries(keys.map(key => {
        const value = Number(data[key]);
        return [key, Number.isFinite(value) ? Math.max(0, value) : 0];
      })));
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Unable to load device dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return (
    <div className="device-dashboard"><div className="dd-loading" role="status">
      <RefreshCw size={26} className="dd-spin" />
      <h3>Loading device overview</h3><p>Fetching the latest production counts.</p>
    </div></div>
  );

  if (!dashboard) return (
    <div className="device-dashboard"><div className="dd-loading" role="alert">
      <XCircle size={28} /><h3>Unable to load dashboard</h3><p>{error}</p>
      <button className="dd-button" onClick={() => fetchDashboard()}><RefreshCw size={16} />Try again</button>
    </div></div>
  );

  const total = dashboard.getalldevices;
  const completion = percent(dashboard.testcompleted, total);
  const active = dashboard.undertesting + dashboard.continuetesting + dashboard.readytoinstall;

  return (
    <div className="device-dashboard" aria-busy={refreshing}>
      <header className="dd-header">
        <div className="dd-heading"><span className="dd-heading-icon"><Settings2 size={23} /></span>
          <div><span className="dd-eyebrow">DEVICE OPERATIONS</span><h2>Device overview</h2><p>Production progress and regional inventory at a glance.</p></div>
        </div>
        <div className="dd-header-actions">
          {lastUpdated && <span className="dd-updated">Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
          <button type="button" className="dd-button" onClick={() => fetchDashboard(true)} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'dd-spin' : ''} />{refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </header>

      {error && <div className="dd-error" role="alert"><XCircle size={18} /><span>{error} Showing the last loaded counts.</span></div>}

      <section className="dd-region-grid" aria-label="Regional inventory">
        <article className="dd-total-card">
          <div className="dd-card-top"><span>Total devices</span><Package size={21} /></div>
          <strong>{formatNumber(total)}</strong><p>Across all device regions</p>
          <div className="dd-total-footer"><span>Inventory overview</span><Globe2 size={16} /></div>
        </article>
        {[{ key: 'indiaversion', label: 'India', abbreviation: 'IN' }, { key: 'usversion', label: 'United States', abbreviation: 'US' }].map(region => (
          <article className="dd-region-card" key={region.key}>
            <div className="dd-card-top"><span className="dd-region-label"><span className="dd-region-badge">{region.abbreviation}</span>{region.label}</span><Globe2 size={18} /></div>
            <strong>{formatNumber(dashboard[region.key])}</strong><p>Devices in this region</p>
            <div className="dd-region-footer"><span>Share of total inventory</span><b>{percent(dashboard[region.key], total)}%</b></div>
            <div className="dd-track" aria-hidden="true"><span style={{ width: `${percent(dashboard[region.key], total)}%` }} /></div>
          </article>
        ))}
      </section>

      <div className="dd-section-heading"><h3 id="dd-regional-testing-heading">Test completion by region</h3><span>Devices with testing completed</span></div>
      <section className="dd-regional-testing-grid" aria-labelledby="dd-regional-testing-heading">
        {[
          { key: 'indiatestcompleted', label: 'India', abbreviation: 'IN' },
          { key: 'ustestcompleted', label: 'United States', abbreviation: 'US' }
        ].map(region => (
          <article className="dd-kpi dd-tone-completed" key={region.key}>
            <div className="dd-card-top">
              <span className="dd-region-label"><span className="dd-region-badge">{region.abbreviation}</span>{region.label}</span>
              <span className="dd-icon"><CheckCircle2 size={19} aria-hidden="true" /></span>
            </div>
            <strong>{formatNumber(dashboard[region.key])}</strong>
            <h4>Test completed</h4>
            <p>{region.label} devices that have completed testing</p>
          </article>
        ))}
      </section>

      <div className="dd-section-heading"><h3>Production pipeline</h3><span>Current device status</span></div>
     <section className="dd-kpi-grid" aria-label="Production pipeline">
  {PRODUCTION_STATUSES.map(
    ({ key, label, description, icon: Icon, tone }) => (
      <article
        className={`dd-kpi dd-tone-${tone}`}
        key={key}
      >
        <div className="dd-card-top">
          <span className="dd-icon">
            <Icon size={19} />
          </span>

          <span className="dd-kpi-share">
            {percent(dashboard[key], total)}% of total
          </span>
        </div>

        <strong>{formatNumber(dashboard[key])}</strong>

        <h4>{label}</h4>

        <p>{description}</p>
      </article>
    )
  )}
</section>

      <div className="dd-content-grid">
        <section className="dd-panel">
          <div className="dd-panel-heading"><div><span className="dd-eyebrow">BREAKDOWN</span><h3>Status distribution</h3></div><Activity size={20} /></div>
          <div className="dd-status-list">
            {STATUSES.map(({ key, label, icon: Icon, tone }) => (
              <div className={`dd-status-row dd-tone-${tone}`} key={key}>
                <span className="dd-icon"><Icon size={17} /></span>
                <div className="dd-status-details"><div className="dd-status-label"><span>{label}</span><strong>{formatNumber(dashboard[key])}<small>{percent(dashboard[key], total)}%</small></strong></div>
                  <div className="dd-track" aria-hidden="true"><span style={{ width: `${percent(dashboard[key], total)}%` }} /></div>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="dd-panel dd-summary-panel">
          <div className="dd-panel-heading"><div><span className="dd-eyebrow">PROGRESS</span><h3>Production summary</h3></div><TrendingUp size={20} /></div>
          <div className="dd-completion"><div className="dd-completion-ring" style={{ '--completion': `${completion}%` }}><div><strong>{completion}%</strong><span>completed</span></div></div>
            <div><h4>Testing completion</h4><p><b>{formatNumber(dashboard.testcompleted)}</b> of {formatNumber(total)} devices have completed testing.</p></div>
          </div>
          <div className="dd-summary-row"><span><Activity size={17} />Active production</span><strong>{formatNumber(active)}</strong></div>
          <div className="dd-summary-row"><span><CheckCircle2 size={17} />Completed</span><strong>{formatNumber(dashboard.testcompleted)}</strong></div>
          <div className="dd-summary-row"><span><PauseCircle size={17} />Stopped / paused</span><strong>{formatNumber(dashboard.stopped)}</strong></div>
           <div className="dd-summary-row"><span><PauseCircle size={17} />Installed</span><strong>{formatNumber(dashboard.installed)}</strong></div>
        </section>
      </div>
      <section className="dd-registration">
        <span className="dd-registration-icon"><TrendingUp size={25} /></span>
        <div><span className="dd-eyebrow">LAST 30 DAYS</span><h3>New device registrations</h3><p>Devices added during the last 30 days.</p></div>
        <div className="dd-registration-count"><strong>{formatNumber(dashboard.latest30daysregistered)}</strong><span>devices registered</span></div>
        <ArrowUpRight size={22} aria-hidden="true" />
      </section>
    </div>
  );
};

export default DeviceDashboard;
